import {
  createVaultItem as defaultCreate,
  deleteVaultItem as defaultDelete,
  getVaultItem as defaultGet,
  listVaultItems as defaultList,
  listTrashItems as defaultListTrash,
  permanentlyDeleteTrashItem as defaultPermanentDelete,
  restoreTrashItem as defaultRestore,
  updateVaultItem as defaultUpdate,
} from '../api/vaultApi'
import { decryptVaultPayload, encryptVaultPayload } from './vaultCipher'
import {
  type VaultCipherBlob,
  type VaultItem,
  type VaultItemEncryptedResponse,
  type VaultItemPayload,
  type VaultItemType,
} from '../types'

/**
 * Vault のオーケストレータ (T-D-Vault-A、UI 側 hooks の API 表面)。
 *
 * 設計判断:
 *   - 「暗号化 / 復号」と「REST 呼出」を 1 関数に閉じ込め、UI hook 側は SK と
 *     payload を渡すだけで CRUD が完結する。
 *   - 全関数で deps 注入を許す (`VaultFlowDeps`)、spec 側は network なしで
 *     pure な branch カバレッジを取れる。
 *   - 復号失敗 (auth tag 不一致 / JSON corruption) は **個別アイテムを skip**
 *     して全体 list を返す `decryptDecodeFailed` の集計 model にする。1 件壊れて
 *     も他のアイテムが見えなくならない UX を担保。
 */

export interface VaultFlowDeps {
  list: typeof defaultList
  get: typeof defaultGet
  create: typeof defaultCreate
  update: typeof defaultUpdate
  delete: typeof defaultDelete
  listTrash: typeof defaultListTrash
  restoreTrash: typeof defaultRestore
  permanentDeleteTrash: typeof defaultPermanentDelete
}

const defaultDeps = (): VaultFlowDeps => ({
  list: defaultList,
  get: defaultGet,
  create: defaultCreate,
  update: defaultUpdate,
  delete: defaultDelete,
  listTrash: defaultListTrash,
  restoreTrash: defaultRestore,
  permanentDeleteTrash: defaultPermanentDelete,
})

/**
 * 復号失敗を 1 件ごとに分離して結果と一緒に返す。
 *
 * `items` は復号成功分のみ (updatedAt 降順)、`failures` は復号失敗の id + 理由を含む。
 */
export interface VaultListResult {
  readonly items: readonly VaultItem[]
  readonly failures: readonly VaultDecryptFailure[]
}

export interface VaultDecryptFailure {
  readonly id: string
  readonly type: VaultItemType
  readonly reason: string
}

const decryptOne = async (
  symmetricKey: Uint8Array,
  resp: VaultItemEncryptedResponse,
): Promise<VaultItem | VaultDecryptFailure> => {
  try {
    const payload = await decryptVaultPayload(symmetricKey, resp.cipher)

    return {
      id: resp.id,
      type: resp.type,
      folderId: resp.folderId,
      payload,
      createdAt: resp.createdAt,
      updatedAt: resp.updatedAt,
      deletedAt: resp.deletedAt,
    } satisfies VaultItem
  } catch (err) {
    return {
      id: resp.id,
      type: resp.type,
      reason: err instanceof Error ? err.message : String(err),
    } satisfies VaultDecryptFailure
  }
}

const isVaultItem = (v: VaultItem | VaultDecryptFailure): v is VaultItem => 'payload' in v

const sortByUpdatedDesc = (a: VaultItem, b: VaultItem): number =>
  b.updatedAt.localeCompare(a.updatedAt)

/**
 * GET /vault/items → 全件復号 → updatedAt 降順 で返す。
 *
 * 復号失敗 (auth tag 不一致 / JSON 破損) は `failures` に分離して返却 (例外を上げない)。
 */
export const loadVaultList = async (
  symmetricKey: Uint8Array,
  deps: VaultFlowDeps = defaultDeps(),
): Promise<VaultListResult> => {
  const responses = await deps.list()
  const decrypted = await Promise.all(responses.map(resp => decryptOne(symmetricKey, resp)))
  const items = decrypted.filter(isVaultItem).sort(sortByUpdatedDesc)
  const failures = decrypted.filter((v): v is VaultDecryptFailure => !isVaultItem(v))

  return { items, failures }
}

/**
 * GET /vault/items/:id → 1 件復号。復号失敗は throw する (UI 側で error 描画)。
 */
export const loadVaultItem = async (
  symmetricKey: Uint8Array,
  id: string,
  deps: VaultFlowDeps = defaultDeps(),
): Promise<VaultItem> => {
  const resp = await deps.get(id)
  const payload = await decryptVaultPayload(symmetricKey, resp.cipher)

  return {
    id: resp.id,
    type: resp.type,
    folderId: resp.folderId,
    payload,
    createdAt: resp.createdAt,
    updatedAt: resp.updatedAt,
    deletedAt: resp.deletedAt,
  }
}

/**
 * 平文 payload を暗号化 → POST /vault/items → 復号して返す。
 *
 * POST 後の復号は wire 上の cipher を確認するためで、サーバが iv を入れ替えた
 * 等の異常を即検出できる (= 自分が送った cipher と同じものが返ってくるはず)。
 */
export const createVaultItemEncrypted = async (
  symmetricKey: Uint8Array,
  payload: VaultItemPayload,
  folderId: string | null = null,
  deps: VaultFlowDeps = defaultDeps(),
): Promise<VaultItem> => {
  const cipher: VaultCipherBlob = await encryptVaultPayload(symmetricKey, payload)
  const resp = await deps.create({ type: payload.type, cipher, folderId })
  // サーバが cipher を改変しない前提だが、念のため復号して shape を確認する。
  const decrypted = await decryptVaultPayload(symmetricKey, resp.cipher)

  return {
    id: resp.id,
    type: resp.type,
    folderId: resp.folderId,
    payload: decrypted,
    createdAt: resp.createdAt,
    updatedAt: resp.updatedAt,
    deletedAt: resp.deletedAt,
  }
}

/** PUT /vault/items/:id — 全置換更新。 */
export const updateVaultItemEncrypted = async (
  symmetricKey: Uint8Array,
  id: string,
  payload: VaultItemPayload,
  folderId: string | null = null,
  deps: VaultFlowDeps = defaultDeps(),
): Promise<VaultItem> => {
  const cipher: VaultCipherBlob = await encryptVaultPayload(symmetricKey, payload)
  const resp = await deps.update(id, { type: payload.type, cipher, folderId })
  const decrypted = await decryptVaultPayload(symmetricKey, resp.cipher)

  return {
    id: resp.id,
    type: resp.type,
    folderId: resp.folderId,
    payload: decrypted,
    createdAt: resp.createdAt,
    updatedAt: resp.updatedAt,
    deletedAt: resp.deletedAt,
  }
}

/** DELETE /vault/items/:id — アイテムをゴミ箱へ移動 (soft-delete、backend 204)。 */
export const deleteVaultItemById = (
  id: string,
  deps: VaultFlowDeps = defaultDeps(),
): Promise<void> => deps.delete(id)

/**
 * GET /vault/trash → ゴミ箱内アイテムを全件復号して返す (削除日の新→旧)。
 *
 * 並び順はサーバ (`deletedAt desc`) に従う。復号失敗は `failures` に分離 (例外を上げない)。
 */
export const loadTrashList = async (
  symmetricKey: Uint8Array,
  deps: VaultFlowDeps = defaultDeps(),
): Promise<VaultListResult> => {
  const responses = await deps.listTrash()
  const decrypted = await Promise.all(responses.map(resp => decryptOne(symmetricKey, resp)))
  const items = decrypted.filter(isVaultItem)
  const failures = decrypted.filter((v): v is VaultDecryptFailure => !isVaultItem(v))

  return { items, failures }
}

/** POST /vault/trash/:id/restore — ゴミ箱内アイテムを通常状態へ復元。 */
export const restoreVaultItemById = async (
  id: string,
  deps: VaultFlowDeps = defaultDeps(),
): Promise<void> => {
  await deps.restoreTrash(id)
}

/** DELETE /vault/trash/:id — ゴミ箱内アイテムを物理削除 (復元不可、backend 204)。 */
export const permanentlyDeleteVaultItemById = (
  id: string,
  deps: VaultFlowDeps = defaultDeps(),
): Promise<void> => deps.permanentDeleteTrash(id)
